import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  MessageCircle,
  Sparkles,
  MapPin,
  Clock,
  Send,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import SiteFooter from '../components/SiteFooter';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';

const REASONS = ['Sales', 'Support', 'Partnership', 'Press', 'Other'];

export default function ContactPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', reason: 'Sales', message: '' });
  const [state, setState] = useState('idle'); // idle | sending | sent | error

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.message.trim()) return;
    setState('sending');
    try {
      // Reuse the leads endpoint — one payload with all details
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          name: form.name || undefined,
          source: `contact-${form.reason.toLowerCase()}`,
        }),
      });
      setState('sent');
    } catch {
      setState('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF007F 0%, transparent 70%)' }}
      />

      <header className="relative z-10 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              data-testid="contact-back-btn"
              className="text-white/60 hover:text-white transition"
            >
              <ArrowLeft size={18} />
            </button>
            <BrandLogo size={30} />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.25em] text-white/60 mb-4">
            <MessageCircle size={11} /> We&rsquo;re here to help
          </div>
          <h1 className="text-5xl md:text-6xl font-display font-black tracking-tight leading-[0.95]">
            Let&rsquo;s talk.
          </h1>
          <p className="mt-4 text-lg text-white/60">
            Sales, support, partnerships, press — pick your reason and we&rsquo;ll get back within 24h.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Info cards */}
          <div className="md:col-span-1 space-y-3">
            {[
              {
                icon: Mail,
                title: 'Email us',
                lines: ['hello@aroundtheworldai.com', 'Reply within 24h'],
                testid: 'contact-info-email',
              },
              {
                icon: Clock,
                title: 'Support hours',
                lines: ['Mon–Fri · 9am–7pm UTC', 'Weekends via email only'],
                testid: 'contact-info-hours',
              },
              {
                icon: MapPin,
                title: 'Based worldwide',
                lines: ['Fully remote team', 'Working from 6 continents'],
                testid: 'contact-info-location',
              },
              {
                icon: Sparkles,
                title: 'Try the AI',
                lines: ['Or launch the builder', 'and ask AI directly'],
                testid: 'contact-info-ai',
                onClick: () => navigate('/build'),
              },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <button
                  key={i}
                  onClick={c.onClick}
                  data-testid={c.testid}
                  className={`w-full text-left rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition ${
                    c.onClick ? 'hover:bg-white/[0.05] hover:border-white/20 cursor-pointer' : ''
                  }`}
                >
                  <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center text-[#00F0FF] mb-3"
                    style={{ background: 'rgba(0,240,255,0.1)' }}
                  >
                    <Icon size={16} />
                  </div>
                  <div className="font-bold text-white">{c.title}</div>
                  {c.lines.map((l, j) => (
                    <div key={j} className="text-[13px] text-white/55 mt-0.5">
                      {l}
                    </div>
                  ))}
                </button>
              );
            })}
          </div>

          {/* Form */}
          <div className="md:col-span-2">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 md:p-8"
            >
              {state === 'sent' ? (
                <div className="text-center py-10" data-testid="contact-sent-view">
                  <div
                    className="h-14 w-14 mx-auto rounded-2xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)' }}
                  >
                    <CheckCircle2 size={26} className="text-black" strokeWidth={2.5} />
                  </div>
                  <h2 className="mt-5 text-2xl font-display font-black text-white">
                    Message sent ✨
                  </h2>
                  <p className="mt-2 text-white/60 max-w-md mx-auto">
                    Thanks {form.name || 'friend'} — we got your note and will reply within 24h at{' '}
                    <span className="font-mono text-white/85">{form.email}</span>.
                  </p>
                  <button
                    onClick={() => {
                      setState('idle');
                      setForm({ name: '', email: '', reason: 'Sales', message: '' });
                    }}
                    data-testid="contact-send-another"
                    className="mt-6 text-sm text-white/60 hover:text-white transition"
                  >
                    ← Send another message
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-white/45 mb-1.5 font-mono">
                        Your name
                      </label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Alex Chen"
                        data-testid="contact-form-name"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-white/45 mb-1.5 font-mono">
                        Email *
                      </label>
                      <Input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@company.com"
                        data-testid="contact-form-email"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-[10px] uppercase tracking-widest text-white/45 mb-1.5 font-mono">
                      What&rsquo;s this about?
                    </label>
                    <div className="flex flex-wrap gap-1.5" data-testid="contact-form-reasons">
                      {REASONS.map((r) => (
                        <button
                          type="button"
                          key={r}
                          onClick={() => setForm({ ...form, reason: r })}
                          data-testid={`contact-reason-${r.toLowerCase()}`}
                          className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-medium border transition ${
                            form.reason === r
                              ? 'text-black border-transparent'
                              : 'text-white/70 border-white/10 hover:bg-white/[0.06]'
                          }`}
                          style={
                            form.reason === r
                              ? { background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }
                              : {}
                          }
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-[10px] uppercase tracking-widest text-white/45 mb-1.5 font-mono">
                      Message *
                    </label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us what you need — the more context, the better."
                      required
                      rows={5}
                      data-testid="contact-form-message"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[130px] resize-none"
                    />
                  </div>

                  {state === 'error' && (
                    <div className="mt-4 text-[13px] text-red-300">
                      Something went wrong sending your message. Please try again in a moment or
                      email us directly at hello@aroundtheworldai.com.
                    </div>
                  )}

                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-[11px] text-white/40">
                      By submitting, you agree to our{' '}
                      <a href="/privacy" className="underline hover:text-white">
                        Privacy Policy
                      </a>
                      .
                    </div>
                    <button
                      type="submit"
                      disabled={
                        state === 'sending' || !form.email.trim() || !form.message.trim()
                      }
                      data-testid="contact-form-submit"
                      className="rounded-full px-5 py-2.5 text-sm font-semibold text-black disabled:opacity-50 flex items-center gap-2"
                      style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
                    >
                      {state === 'sending' ? (
                        <>
                          <Loader2 size={14} className="animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          <Send size={14} /> Send message
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
